import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import type { TenantCloneCleanupStep, TenantCloneJob } from '@bliks-io/contracts';
import { Alert, AlertDescription, AlertTitle, Button } from '@bliks-io/ui';

const CLEANUP_STEP_LABEL_KEYS: Record<TenantCloneCleanupStep, string> = {
  revokeMemberships: 'tenantAdmin.cloneCleanupStepRevokeMemberships',
  deactivateZitadelOrg: 'tenantAdmin.cloneCleanupStepDeactivateZitadelOrg',
  dropDatabase: 'tenantAdmin.cloneCleanupStepDropDatabase',
  deleteTenantRow: 'tenantAdmin.cloneCleanupStepDeleteTenantRow'
};

interface CloneJobAlertProps {
  job: TenantCloneJob;
  onDiscard: () => void;
  onDismiss: () => void;
  isDismissing: boolean;
}

/**
 * The operator's decision point for a clone that will never finish on its own.
 *
 * An `interrupted` job is NOT a deletion: the reaper only parks a job whose
 * heartbeat went stale, because a long silent phase (a sequential user import)
 * is indistinguishable from a dead worker. The half-built tenant is still
 * there, inactive and inert, until somebody decides — so the copy says so, and
 * neither action is taken implicitly.
 *
 * A discard that only partly succeeded keeps the job AND its original status,
 * writing `cleanupStatus` and nothing else — so `status` alone would keep
 * claiming "nothing was deleted" about a tenant whose database is already gone.
 * A cleanup record with a FAILED step therefore takes precedence over `status`
 * here, and the copy switches to what is actually left. Presence alone is not
 * the signal: it says a discard was attempted, not that it left anything behind.
 *
 * Only `interrupted` and `failed` jobs belong here. A clone whose tenant was
 * KEPT (`importFailed`, `completed`) is active and usable; it gets a row badge
 * with its own dismiss, and the discard endpoint refuses it by design.
 */
export function CloneJobAlert({ job, onDiscard, onDismiss, isDismissing }: CloneJobAlertProps) {
  const { t } = useTranslation();

  const cleanupSteps = job.cleanupStatus?.steps;
  const failedCleanupSteps = cleanupSteps?.filter((step) => step.outcome === 'failed') ?? [];
  // Every discard guard reads the ownership nonce off the tenant row, so once
  // that row is gone a retry can only ever be refused — offering one would be
  // the same kind of lie as the stale status copy above.
  const tenantRowRemains =
    cleanupSteps?.find((step) => step.step === 'deleteTenantRow')?.outcome !== 'succeeded';

  const copy =
    failedCleanupSteps.length > 0
      ? {
          colorScheme: 'error' as const,
          title: t('tenantAdmin.cloneDiscardPartialAlertTitle', { name: job.tenantName }),
          body: t('tenantAdmin.cloneDiscardPartialAlert', {
            steps: failedCleanupSteps
              .map((step) => t(CLEANUP_STEP_LABEL_KEYS[step.step]))
              .join(', ')
          }),
          note: t(
            tenantRowRemains
              ? 'tenantAdmin.cloneDiscardPartialRetry'
              : 'tenantAdmin.cloneDiscardPartialManual'
          )
        }
      : {
          colorScheme: job.status === 'interrupted' ? ('warning' as const) : ('error' as const),
          title: t(
            job.status === 'interrupted'
              ? 'tenantAdmin.cloneInterruptedAlertTitle'
              : 'tenantAdmin.cloneFailedAlertTitle',
            { name: job.tenantName }
          ),
          body: t(
            job.status === 'interrupted'
              ? 'tenantAdmin.cloneInterruptedAlert'
              : 'tenantAdmin.cloneFailedAlert',
            { slug: job.tenantSlug }
          ),
          note: undefined
        };

  return (
    <Alert colorScheme={copy.colorScheme}>
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>{copy.title}</AlertTitle>
      <AlertDescription>
        <p>{copy.body}</p>
        {copy.note && <p className="mt-1">{copy.note}</p>}
        <div className="mt-3 flex flex-wrap gap-2">
          {tenantRowRemains && (
            <Button size="small" variant="destructive" onClick={onDiscard}>
              {t('tenantAdmin.cloneDiscard')}
            </Button>
          )}
          <Button size="small" variant="outline" onClick={onDismiss} loading={isDismissing}>
            {t('tenantAdmin.cloneDismiss')}
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
